import { Link } from 'react-router-dom';
import { Instagram, Facebook } from 'lucide-react';
import Logo from './Logo';
import { LangSwitch } from './Navbar';
import { useSite } from '../context/SiteContext';
import { pathFor } from '../i18n/routes';

export default function Footer() {
  const { lang, t } = useSite();
  const navKeys = ['home', 'media', 'souvenir', 'portfolio', 'about', 'contact'];
  const legalKeys = ['privacy', 'cookies', 'terms'];

  return (
    <footer className="border-t border-zinc-900 bg-black" data-testid="footer">
      <div className="mx-auto max-w-[1600px] px-5 py-16 sm:px-8">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <Link to={pathFor('home', lang)} aria-label="F&L Group" data-testid="footer-logo">
              <Logo />
            </Link>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
              {t.footer.tagline}
            </p>
            <a
              href="mailto:geral@flgroup.pt"
              data-testid="footer-email"
              className="mt-4 inline-block font-mono text-xs tracking-widest text-zinc-400 underline decoration-zinc-700 underline-offset-4 transition-colors hover:text-white"
            >
              geral@flgroup.pt
            </a>
            <a
              href="tel:+351915562805"
              data-testid="footer-phone"
              className="mt-2 inline-block font-mono text-xs tracking-widest text-zinc-400 underline decoration-zinc-700 underline-offset-4 transition-colors hover:text-white"
            >
              +351 915 562 805
            </a>
          </div>

          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3">
            <div>
              <p className="kicker mb-4">{t.footer.navigation}</p>
              <ul className="space-y-2.5">
                {navKeys.map((key) => (
                  <li key={key}>
                    <Link
                      to={pathFor(key, lang)}
                      data-testid={`footer-nav-${key}`}
                      className="text-sm text-zinc-400 transition-colors hover:text-white"
                    >
                      {t.nav[key]}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="kicker mb-4">{t.footer.legal}</p>
              <ul className="space-y-2.5">
                {legalKeys.map((key) => (
                  <li key={key}>
                    <Link
                      to={pathFor(key, lang)}
                      data-testid={`footer-legal-${key}`}
                      className="text-sm text-zinc-400 transition-colors hover:text-white"
                    >
                      {t.legal[key].title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="kicker mb-4">{t.footer.follow}</p>
              <div className="flex gap-3">
                {[
                  { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/f.l.group/', id: 'social-instagram' },
                  { icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/people/FL-Group/61594664982999/', id: 'social-facebook' },
                ].map(({ icon: Icon, label, href, id }) => (
                  <a
                    key={id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={id}
                    className="flex h-10 w-10 items-center justify-center border border-zinc-800 text-zinc-400 transition-colors duration-300 hover:text-black brand-border hover:!bg-[var(--brand)]"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-zinc-900 pt-6 sm:flex-row sm:items-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600" data-testid="footer-copyright">
            {t.footer.rights}
          </p>
          <LangSwitch />
        </div>
      </div>
    </footer>
  );
}
